import { memo } from 'react';
import { useRecoilValue } from 'recoil';
import { Redirect } from 'react-router-dom';

import * as K from 'common/constants';
import { HELP_ROUTE } from 'components/help/constants';
import { accountDataState } from 'components/account/account.state';
import { routeState } from 'components/common/Managers/RouteStateManager.state';
import { isRouteEnabledState, isRouterReadyState } from 'components/common/Router/Router.state';
import { settingsState } from 'components/common/Managers/SettingsManager.state';

// Components
import PageNotFound from 'components/other/404/PageNotFound';
import HelpPage from 'components/help/HelpPage';
import AccessDeniedError from 'components/common/AccessDeniedError/AccessDeniedError';
import { getRouteComponent } from 'components/common/Router/Router.routes';

/**
 * Router component for the {@link PrivateApp}. The Router reads from recoil state; if the actual URL is ahead of or
 * behind the recoil state store for any reason, this component won't know anything about it. This component is always
 * memoized, and thus only renders when the recoil state it subscribes to changes (usually, the route).
 *
 * In the future, react-router might be removed, but for now the router from react-router is a parent of our router. The
 * react-router renders ahead of ours, and thus the memoization effectively "quarantines" the rest of the render tree
 * from react-router renders, and instead waits for recoil state updates. Importantly, this means that both the
 * react-router and history APIs should be used in a write-only fashion in this component's children.
 */
function Router() {
  const route = useRecoilValue(routeState);
  const isRouteEnabled = useRecoilValue(isRouteEnabledState);
  const accountData = useRecoilValue(accountDataState);
  const settings = useRecoilValue(settingsState);

  const { pagesExcludedForTrialEmailVerification = [] } = settings;

  // "Suspend" until isRouterReady === true. The router itself needs to wait on account and other data to behave
  // correctly, but it is worth pointing out that several children need this data to safely render as well.
  const isRouterReady = useRecoilValue(isRouterReadyState);
  if (!isRouterReady) {
    return <></>;
  }

  // If email verification required – block user from accessing the UI, redirect to the verification page
  //  but allow certain routes that do not need email verification (e.g. '/verifyEmail' or '/emailVerificationRequired' itself)
  const routesWithoutEmailVerification = [K.ROUTE.EMAIL_VERIFICATION].concat(
    pagesExcludedForTrialEmailVerification?.map((r: string) => `/${r}` as K.ROUTE) || [],
  );
  const isRouteExcludedFromEmailVerification =
    routesWithoutEmailVerification && routesWithoutEmailVerification.includes(route);
  const { isEmailVerified, isEmailVerificationRequired } = accountData;
  if (isEmailVerified === false && isEmailVerificationRequired && !isRouteExcludedFromEmailVerification) {
    return <Redirect to={K.ROUTE.EMAIL_VERIFICATION} />;
  }

  const page = Object.values(HELP_ROUTE).includes(route as unknown as HELP_ROUTE) ? (
    <HelpPage />
  ) : (
    getRouteComponent(route)
  );

  // If user has access to a page and route is valid
  if (isRouteEnabled && page) {
    return page;
  }

  // if route is valid but not enabled show access denied , if route is not valid show 404
  return page ? <AccessDeniedError /> : <PageNotFound />;
}

// Router should never render because of a parent render. It should only render when its own internal state changes.
// This probably can be removed if we remove recoil-router, since suppressing those renders is its main purpose.
const alwaysTreatPropsAsEqual = () => true;

export default memo(Router, alwaysTreatPropsAsEqual);
